<template>
  <splide
    ref="splide"
    :options="splideOptions"
    :has-track="false"
    class="product-slides"
    :class="$blockClass(blockName, 'product-slides')"
    @splide:mounted="instance => $emit('mounted', instance)"
    @splide:moved="trackClick"
  >
    <splide-track>
      <splide-slide
        v-for="product of products"
        :key="product.id"
        @click="$emit('product-clicked', product)"
      >
        <product-card
          :class="$blockClass(blockName, 'card')"
          :block-class="blockName"
          :product="product"
          :image-options="{
            sizes: `(min-width: ${breakpoints.l}px) 30vw, 100vw`,
            minMax: '390-1024',
          }"
          :source="source"
        />
      </splide-slide>
    </splide-track>

    <div class="splide__arrows splide__arrows--ltr">
      <button
        type="button"
        class="splide__arrow splide__arrow--prev"
        :aria-label="$string('accessibility.carousel.previous')"
        @click="$emit('navigation-clicked', false)"
      >
        <icon-arrow-left />
      </button>

      <button
        type="button"
        class="splide__arrow splide__arrow--next"
        :aria-label="$string('accessibility.carousel.next')"
        @click="$emit('navigation-clicked', false)"
      >
        <icon-arrow-right />
      </button>
    </div>
  </splide>
</template>

<script>
/**
 * Vue: product-slides (product-slides)
 * -----------------------------------------------------------------------------
 * Slides that feature product cards.
 * @param {Array} products - Array of products to display.
 * @param {Object} options - Options for the Splide carousel.
 * @param {String} blockName - Name of the block.
 *
 */

import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapState } from 'vuex'

import { values as breakpoints } from '~/config/breakpoints'
import { track } from '~/helpers/datalayer-actions'

import ProductCard from '~async/product-card/product-card'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'

export default {
  name: 'ProductSlides',
  components: {
    ProductCard,
    IconArrowLeft,
    IconArrowRight,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    products: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    blockName: {
      type: String,
      default: '',
      required: true,
    },
    source: {
      type: String,
      default: '',
    },
  },

  emits: {
    'mounted': (instance) => Boolean(instance),
    'product-clicked': (product) => Boolean(product),
    'navigation-clicked': (direction) => typeof direction !== 'undefined',
  },

  data() {
    return {
      breakpoints,
    }
  },

  computed: {

    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Merge splideOptions with options passed in as props.
     * @return {Object}
     */
    splideOptions() {
      return {
        mediaQuery: 'min',
        flickMaxPages: 1,
        focus: 'left',
        gap: 'var(--spacing-m)',
        perMove: 1,
        perPage: 1,
        wheel: false,
        arrows: true,
        drag: true,
        flickPower: 200,
        pagination: true,
        type: 'slide',
        omitEnd: true,
        autoplay: false,
        respectReducedMotion: true,
        breakpoints: {
          [breakpoints.m]: {
            gap: 'var(--spacing-xl)',
            perPage: 1,
          },
          [breakpoints.l]: {
            gap: 'var(--spacing-xl)',
            keyboard: true,
            perMove: 1,
            perPage: 3.1,
          },
        },
        ...this.options,
      }
    },
  },

  watch: {

    /**
     * Watch viewport to toggle dragging
     * @param {boolean} mobile - Whether the viewport is mobile size
     */
    isMobile(mobile) {
      this.$refs.splide.splide.Components.Drag.disable(!mobile && this.products.length <= 3)
    },

    /**
     * Watch products to toggle dragging
     */
    products() {
      if (this.isMobile) {
        return
      }
      this.$refs.splide.splide.Components.Drag.disable(this.products.length <= 3)
    },
  },

  methods: {

    trackClick() {
      if (this.source) {
        track(`${this.source}_carousel_click`)
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-slides';
</style>
